import {SortingState} from '@tanstack/table-core';
import clsx from 'clsx';
import {isEmpty, isEqual} from 'lodash';
import {MRT_RowVirtualizer} from 'material-react-table';
import type {MRT_ColumnDef} from 'material-react-table';
import React, {useCallback, useEffect, useMemo, useRef} from 'react';
import {useShallow} from 'zustand/react/shallow';
import {
  Body,
  Button,
  Headline,
  designSystemToken,
} from '@lightricks/react-design-system';
import AnalyticsService, {
  eventNames,
} from '@/services/analytics/AnalyticsService';
import {FLOW_NAMES} from '@/lib/analytics/analyticsConstants';
import getCreatorProfilePageUrl from '@/utils/getCreatorProfilePageUrl';
import translate from '@/utils/translate';
import {SearchDisplayCreator} from '@/types/models/creator-crm/creatorCrm';
import {ViewType} from '@/types/models/creator-crm/creatorCrmStore';
import {
  BaseFilterOption,
  SingleSelection,
} from '@/types/models/creator-crm/filter';
import {PlatformId} from '@/types/models/creator-crm/filterId';
import {DefaultSortDirection} from '@/types/models/creator-crm/sortField';
import getAnalyticsSearchScreenName from '@/views/creators/creator-crm/utils/getAnalyticsSearchScreenName';
import getProfileImageUrl from '@/views/creators/creator-crm/utils/getProfileImageUrl';
import mapCreatorsForAnalytics from '@/views/creators/creator-crm/utils/mapCreatorsForAnalytics';
import useCreatorsVirtualizedGridProps from '@/views/creators/hooks/use-creators-virtualized-grid-props/useCreatorsVirtualizedGridProps';
import CreatorCard from '@/components/creator-card';
import {
  CreatorCardCtaProps,
  CreatorCardProps,
} from '@/components/creator-card/CreatorCard';
import CreatorCardSkeleton from '@/components/creator-card/CreatorCardSkeleton';
import {CreatorMenuItemProps} from '@/components/creator-card/CreatorOverflowMenuButton';
import Table from '@/components/table';
import VirtualizedGrid from '@/components/virtualized-grid';
import useIsKeyPressed from '@/hooks/use-is-key-pressed';
import useNavigation from '@/hooks/use-navigation';
import usePrevious from '@/hooks/use-previous';
import useCreatorCrmStore, {
  CREATOR_CRM_ITEMS_PER_PAGE,
  creatorCrmActions,
  VIEW_TYPE_GRID,
  VIEW_TYPE_TABLE,
} from '@/stores/creator-crm/creatorCrmStore';
import useEmberIframeStyleStore from '@/stores/emberIframeStyleStore';
import styles from './SearchCreatorsDisplay.module.scss';

const TABLE_ROW_HEIGHT = 50;
const TABLE_SCROLL_TOP_KEY = 'creatorCrmTableScrollTop';
const GRID_SCROLL_TOP_KEY = 'creatorCrmGridScrollTop';
const VETTING_DECISION_BADGE_HEIGHT = 48;
type SearchCreatorsDisplayProps = {
  creatorCardCtaProps?: CreatorCardCtaProps;
  onSortingSelection?: (sortField: string, direction: string) => void;
  searchCreatorsQueryDisabled?: boolean;
  creatorCardOverflowMenuEnabled?: boolean;
  creatorCardOverflowMenuItemsCallBack?: (
    creator: CreatorCardProps
  ) => Promise<CreatorMenuItemProps[]>;
  viewType: ViewType;
  isHeaderHidden?: boolean;
  tableColumns?: MRT_ColumnDef<SearchDisplayCreator>[];
  tableColumnOrder?: string[];
  tableVisibleColumns?: Record<string, boolean>;
  searchDisplayCreators: SearchDisplayCreator[];
  fetchNextPage: () => void;
  hasNextPage?: boolean;
  isSearchCreatorsLoading: boolean;
  isSearchCreatorsFetching: boolean;
  isSearchCreatorsRefetching: boolean;
  loadedPages: number;
  sessionId?: string;
  noResults: boolean;
  onSocialNetworkClick: (name: string, creatorId: string) => void;
  onScroll?: (event: React.UIEvent<HTMLElement>) => void;
  isSelectMode?: boolean;
  onSelectChange?: (id: string, selected: boolean) => void;
  isSearchMode?: boolean;
};

function SearchCreatorsDisplay({
  creatorCardCtaProps,
  onSortingSelection,
  searchCreatorsQueryDisabled,
  creatorCardOverflowMenuEnabled,
  creatorCardOverflowMenuItemsCallBack,
  viewType,
  isHeaderHidden,
  tableColumns,
  tableColumnOrder,
  tableVisibleColumns,
  searchDisplayCreators,
  fetchNextPage,
  hasNextPage,
  isSearchCreatorsLoading,
  isSearchCreatorsFetching,
  isSearchCreatorsRefetching,
  loadedPages,
  sessionId,
  noResults,
  onSocialNetworkClick,
  onScroll,
  isSelectMode,
  onSelectChange,
  isSearchMode,
}: SearchCreatorsDisplayProps) {
  const [
    sessionIdFromStore,
    sorting,
    filterGroups,
    selectedCreators,
    isAllFilterMatchingSelected,
  ] = useCreatorCrmStore(
    useShallow((state) => [
      state.content.sessionId,
      state.content.sorting,
      state.content.filtersState.filtersGroupsSnapshot,
      state.content.selectedCreators,
      state.content.isAllFilterMatchingSelected,
    ])
  );
  const sessionIdResolved = sessionId ?? sessionIdFromStore;
  const previousFilterGroups = usePrevious(filterGroups, filterGroups);
  const {navigate} = useNavigation();
  // Control - Windows Ctrl, Meta - Mac Cmd
  const isCtrlPressed = useIsKeyPressed(['Control', 'Meta']);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const rowVirtualizerRef = useRef<MRT_RowVirtualizer>(null);
  const gridContainerRef = useRef<HTMLDivElement>(null);
  const gridScrollRef = useRef<HTMLDivElement>(null);
  const {sidebarWidth} = useEmberIframeStyleStore();
  let selectedPlatform: PlatformId = 'all';
  if (filterGroups.platforms.filters.platform) {
    selectedPlatform =
      (
        filterGroups.platforms.filters
          .platform as SingleSelection<BaseFilterOption>
      ).selected?.value ?? 'all';
  }
  const isPlatformFiltered = selectedPlatform !== 'all';

  const creatorsVirtualizedGridProps = useCreatorsVirtualizedGridProps(
    gridContainerRef,
    isPlatformFiltered,
    [viewType, sidebarWidth, isSearchMode],
    isSearchMode ? 0 : VETTING_DECISION_BADGE_HEIGHT
  );

  useEffect(() => {
    // autoload next pages if the current loaded data does not fill the table view
    const pagesPerScreen = Math.ceil(
      (tableContainerRef.current?.clientHeight || 0) /
        (TABLE_ROW_HEIGHT * CREATOR_CRM_ITEMS_PER_PAGE)
    );
    if (
      hasNextPage &&
      sessionIdResolved &&
      loadedPages < pagesPerScreen &&
      !isSearchCreatorsFetching &&
      !isSearchCreatorsLoading
    ) {
      fetchNextPage();
    }
  }, [
    hasNextPage,
    sessionIdResolved,
    loadedPages,
    isSearchCreatorsFetching,
    isSearchCreatorsLoading,
    fetchNextPage,
  ]);

  const onSortingChange = (newSorting: SortingState) => {
    if (newSorting?.length) {
      creatorCrmActions.changeSorting(
        newSorting[0].id,
        newSorting[0].desc ? 'DESC' : 'ASC'
      );
      onSortingSelection?.(
        newSorting[0].id,
        newSorting[0].desc ? 'DESC' : 'ASC'
      );
    } else {
      creatorCrmActions.changeSorting('');
      onSortingSelection?.('', DefaultSortDirection);
    }
  };

  const selectedCreatorsMap = useMemo(() => {
    if (isAllFilterMatchingSelected) {
      return Object.fromEntries(
        searchDisplayCreators.map((creator) => [creator.id, true])
      );
    }
    return Object.fromEntries(
      Array.from(selectedCreators, ([key]) => [key, true])
    );
  }, [isAllFilterMatchingSelected, selectedCreators, searchDisplayCreators]);

  const tableSorting = useMemo(() => {
    if (!isEmpty(sorting)) {
      return [
        {
          id: sorting.field,
          desc: (sorting.direction as string) === 'DESC',
        },
      ];
    }
    return [];
  }, [sorting]);

  const onCreatorClick = (creator: SearchDisplayCreator) => {
    const redirectUrl = getCreatorProfilePageUrl.fromCrm(creator.id);

    onCreatorClickAnalytics(JSON.stringify(mapCreatorsForAnalytics([creator])));
    navigate(redirectUrl);
  };

  const onCreatorClickAnalytics = useCallback(
    (creatorId: string) => {
      const recruitmentSearchFlow = AnalyticsService.getOrCreateFlow(
        FLOW_NAMES.RECRUITMENT_SEARCH
      );
      AnalyticsService.dispatchEvent(
        eventNames.RECRUITMENT_PORTFOLIO_PRESENTED,
        {
          flow_id: recruitmentSearchFlow.flow_id,
          screen_presentation_id: recruitmentSearchFlow.screen_presentation_id,
          creator_portfolio_presentation_id: '',
          recruitment_search_id: sessionIdResolved,
          creator_id: creatorId,
          recruitment_screen_name: getAnalyticsSearchScreenName(viewType),
        }
      );
    },
    [viewType, sessionIdResolved]
  );

  const handleOpenFilters = useCallback(() => {
    creatorCrmActions.openFiltersDrawer();
  }, []);

  const handleClearAll = useCallback(() => {
    creatorCrmActions.updateSearchChips([]);
    creatorCrmActions.resetAllFilters();
  }, []);

  const renderEmptyState = () => {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyStateTextBlock}>
          <Headline size="sm" color={designSystemToken('semantic.fg.primary')}>
            {translate('No matches')}
          </Headline>
          <Body size="md" color={designSystemToken('semantic.fg.secondary')}>
            {translate(
              'Adjust your search or loosen a filter to see more creators.'
            )}
          </Body>
        </div>
        <div className={styles.emptyStateActions}>
          <Button variant="contained" size="small" onClick={handleOpenFilters}>
            {translate('Open Filters')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            className={styles.outlinedButton}
            onClick={handleClearAll}
          >
            {translate('Clear All')}
          </Button>
        </div>
      </div>
    );
  };

  const tableContainerClasses = useMemo(() => {
    const isLoadingNotInitial =
      !(isSearchCreatorsLoading || searchCreatorsQueryDisabled) &&
      isSearchCreatorsFetching;
    return clsx(
      styles.tableContainer,
      isLoadingNotInitial && styles.loading,
      styles.lowSearchHeader,
      isHeaderHidden && styles.headerHidden
    );
  }, [
    isSearchCreatorsLoading,
    searchCreatorsQueryDisabled,
    isSearchCreatorsFetching,
    isHeaderHidden,
  ]);

  const gridContainerClasses = useMemo(
    () =>
      clsx(
        styles.gridContainer,
        styles.lowSearchHeader,
        isHeaderHidden && styles.headerHidden,
        selectedCreators.size > 0 && styles.withActionBar
      ),
    [isHeaderHidden, selectedCreators.size]
  );

  const tableClasses = useMemo(
    () => ({
      container: tableContainerClasses,
      paper: styles.tablePaper,
      tableHeadRow: styles.tableHeadRow,
      footer: selectedCreators.size > 0 ? styles.highFooter : '',
    }),
    [tableContainerClasses, selectedCreators.size]
  );

  useEffect(() => {
    if (isEqual(filterGroups, previousFilterGroups)) {
      return;
    }

    rowVirtualizerRef.current?.scrollToIndex(0);
    gridScrollRef.current?.scrollTo(0, 0);
    sessionStorage.setItem(GRID_SCROLL_TOP_KEY, '0');
    sessionStorage.setItem(TABLE_SCROLL_TOP_KEY, '0');
  }, [filterGroups, previousFilterGroups]);

  const renderCreatorCard = useCallback(
    (creator: SearchDisplayCreator) => {
      const matchedPostsImages = (creator.matchedPosts || []).map(
        (matchedPost) => matchedPost.imageUrl
      );

      const creatorCategories = (creator.categories || []).map((category) =>
        translate(category)
      );

      return (
        <CreatorCard
          key={creator.id}
          id={creator.id}
          fullName={creator.fullName}
          email={creator.email}
          profileImage={getProfileImageUrl(creator.profileImage)}
          categories={creatorCategories}
          age={creator.age?.toString()}
          location={creator.location}
          images={
            [...matchedPostsImages, ...(creator.portfolioImages ?? [])].slice(
              0,
              3
            ) as [string, string, string]
          }
          socialNetworks={creator.socialNetworks}
          selectedSocialNetworkProvider={creator.selectedPlatform}
          isOverflowMenuEnabled={creatorCardOverflowMenuEnabled}
          creatorCardCtaProps={creatorCardCtaProps}
          creatorCardOverflowMenuItemsCallBack={
            creatorCardOverflowMenuItemsCallBack
          }
          rating={creator.rating}
          signupDate={creator.signupDate}
          creatorProfileUrl={getCreatorProfilePageUrl.fromCrm(creator.id)}
          onSocialNetworkClick={onSocialNetworkClick}
          onCardClick={onCreatorClickAnalytics}
          isSelectMode={isSelectMode}
          isSelected={!!selectedCreatorsMap[creator.id]}
          onSelectChange={onSelectChange}
          vettingDecision={
            isSearchMode
              ? undefined
              : (creator.brandSafetyVetting?.vettingDecision ??
                'ready_for_review')
          }
        />
      );
    },
    [
      creatorCardOverflowMenuEnabled,
      creatorCardOverflowMenuItemsCallBack,
      creatorCardCtaProps,
      onCreatorClickAnalytics,
      onSocialNetworkClick,
      isSelectMode,
      selectedCreatorsMap,
      onSelectChange,
      isSearchMode,
    ]
  );

  const renderCreatorsDisplayView = () => {
    switch (viewType) {
      case VIEW_TYPE_TABLE: {
        return (
          <div className={styles.searchCreatorTableContainer}>
            <div className={styles.tableWrapper} ref={tableContainerRef}>
              <Table
                data={searchDisplayCreators}
                columns={tableColumns ?? []}
                columnOrder={tableColumnOrder ?? []}
                enableSorting
                isLoadingInitial={
                  (isSearchCreatorsLoading ||
                    searchCreatorsQueryDisabled ||
                    isSearchCreatorsRefetching) &&
                  !searchDisplayCreators.length
                }
                isLoading={isSearchCreatorsFetching}
                showProgressBars={isSearchCreatorsRefetching}
                visibleColumns={tableVisibleColumns ?? {}}
                rowSelection={selectedCreatorsMap}
                sorting={tableSorting}
                onSorting={onSortingChange}
                onRowClick={onCreatorClick}
                isFirstRowBorder={false}
                classes={tableClasses}
                onEndReached={
                  hasNextPage && !isSearchCreatorsFetching
                    ? fetchNextPage
                    : undefined
                }
                enableVirtualization
                rowVirtualizerRef={rowVirtualizerRef}
                tableScrollTopKey={TABLE_SCROLL_TOP_KEY}
                onScroll={onScroll}
              />
            </div>
          </div>
        );
      }
      case VIEW_TYPE_GRID: {
        return (
          <div ref={gridContainerRef}>
            <VirtualizedGrid
              {...creatorsVirtualizedGridProps}
              containerClassName={gridContainerClasses}
              data={searchDisplayCreators}
              onEndReached={
                hasNextPage && !isSearchCreatorsFetching
                  ? fetchNextPage
                  : undefined
              }
              isInitialLoading={
                (isSearchCreatorsLoading || isSearchCreatorsRefetching) &&
                !searchDisplayCreators.length
              }
              showLoader={isSearchCreatorsFetching}
              skeletonsAmount={creatorsVirtualizedGridProps.columns * 2}
              renderItemSkeleton={(index: number) => (
                <CreatorCardSkeleton key={index} />
              )}
              gridScrollRef={gridScrollRef}
              gridScrollTopKey={GRID_SCROLL_TOP_KEY}
              renderItem={renderCreatorCard}
              onScroll={onScroll}
            />
          </div>
        );
      }
      default: {
        return null;
      }
    }
  };

  return noResults ? renderEmptyState() : renderCreatorsDisplayView();
}

export default SearchCreatorsDisplay;
